import { Component, OnDestroy, OnInit, ViewEncapsulation } from '@angular/core';
import {
  FilterData,
  IdentificationData,
  LiveViewCounterChartDTO,
  LiveViewDTO,
  OwnOrganisationsAndSites
} from '../common/types/common';
import { CommonService } from '../common/services/common.service';
import { formatDateToHHmm, onOrganisationChange } from '../common/utils';
import { counterChartMaxData, LIVE_VIEW_FILTER_DATA, OWN_ORGANISATIONS_AND_SITES, toastrConfig } from '../app.config';
import { HttpParams } from '@angular/common/http';
import { ToastrService } from 'ngx-toastr';
import { TranslateService } from '@ngx-translate/core';
import { StatisticsService } from '../common/services/statistics.service';
import { StatisticsDTO } from '../common/types/statistics';
import { forkJoin, Subscription } from 'rxjs';
import { SettingsDTO } from '../common/types/settings';
import { SettingsService } from '../common/services/settings.service';

@Component({
  selector: 'app-live-view',
  templateUrl: './live-view.component.html',
  encapsulation: ViewEncapsulation.None
})
export class LiveViewComponent implements OnInit, OnDestroy {
  isLoading: boolean;
  isLoadingCharts: boolean;
  storedFilters: FilterData;
  ownOrganisationsAndSites: OwnOrganisationsAndSites[];
  organisations: IdentificationData[];
  sites: any[];

  selectedOrganisation: any = {id: null};
  selectedSite: any = {id: null, name: null};
  appliedOrganisation: any = {id: null};
  appliedSite: any = {id: null, name: null};

  settingsData: SettingsDTO[];
  liveViewData: LiveViewDTO[];
  liveViewCounterChartData: LiveViewCounterChartDTO[];
  liveViewChartsData: StatisticsDTO;

  private viewIntervalId: any;
  private chartsIntervalId: any;
  private viewSubscription?: Subscription;
  private chartsSubscription?: Subscription;

  constructor(private commonServices: CommonService,
              private statisticsService: StatisticsService,
              private settingsServices: SettingsService,
              private toastr: ToastrService,
              private translateService: TranslateService) { }

  ngOnInit() {
    this.isLoading = true;
    this.isLoadingCharts = true;
    this.storedFilters = JSON.parse(localStorage.getItem(LIVE_VIEW_FILTER_DATA));
    this.ownOrganisationsAndSites = JSON.parse(localStorage.getItem(OWN_ORGANISATIONS_AND_SITES));
    this.organisations = this.ownOrganisationsAndSites.map(item => item.organisation);

    if (this.organisations.length > 0) {
      this.selectedOrganisation = this.organisations.find(o => o.id === this.storedFilters?.selectedOrganisation?.id) || this.organisations[0];
      this.sites = onOrganisationChange(this.ownOrganisationsAndSites, this.selectedOrganisation.id);
      this.selectedSite = this.sites.find(s => s.id === this.storedFilters?.selectedSite?.id) || null;
      this.updateSites();
    } else {
      this.isLoading = false;
      this.isLoadingCharts = false;
    }
  }

  ngOnDestroy() {
    if (this.viewIntervalId) clearInterval(this.viewIntervalId);
    if (this.chartsIntervalId) clearInterval(this.chartsIntervalId);
    if (this.viewSubscription) this.viewSubscription.unsubscribe();
    if (this.chartsSubscription) this.chartsSubscription.unsubscribe();
  }

  onOrganisationChanged(selectedOrganisation: any) {
    this.selectedOrganisation = selectedOrganisation;
    this.sites = onOrganisationChange(this.ownOrganisationsAndSites, this.selectedOrganisation.id);
    this.selectedSite = null;
  }

  getChartDataForSite(siteId: string): LiveViewCounterChartDTO | undefined {
    return this.liveViewCounterChartData?.find(item => item.id === siteId);
  }

  getSettingsForSite(siteId: string): SettingsDTO | undefined {
    return this.settingsData?.find(setting => setting.siteId === siteId);
  }

  updateSites() {
    this.isLoading = true;
    this.isLoadingCharts = true;
    if (this.viewIntervalId) clearInterval(this.viewIntervalId);
    if (this.chartsIntervalId) clearInterval(this.chartsIntervalId);

    this.appliedOrganisation = this.selectedOrganisation;
    this.appliedSite = this.selectedSite;

    this.settingsData = null;
    this.liveViewData = null;
    this.liveViewCounterChartData = null;
    this.liveViewChartsData = null;

    const filterDataToSave: FilterData = {
      selectedOrganisation: this.appliedOrganisation,
      selectedSite: this.appliedSite
    };
    localStorage.setItem(LIVE_VIEW_FILTER_DATA, JSON.stringify(filterDataToSave));

    const params = new HttpParams()
      .set('organisation', this.appliedOrganisation.id)
      .set('site', (this.appliedSite ? this.appliedSite.id : 'ALL'));

    forkJoin({
      settings: this.settingsServices.getSettings(params),
      view: this.commonServices.getLiveView(params)
    }).subscribe({
      next: (response: any) => {
        this.settingsData = response.settings;
        this.liveViewData = response.view;
        this.liveViewCounterChartData = this.liveViewData;
        this.liveViewCounterChartData.forEach(site => {
          site.chartData = site.chartData || [];
          for (let i = counterChartMaxData; i > 0; i--) {
            site.chartData.push({ down: 0, time: null, count: 0 });
          }
        });
        this.updateLiveCounterChartData();
        this.isLoading = false;
      },
      error: () => {
        this.toastr.error(this.translateService.instant("COMMON.GET_DATA_ERROR"), "", toastrConfig);
        this.isLoading = false;
      }
    });

    this.viewIntervalId = setInterval(() => {
      this.updateView();
    }, 5000); // 5 ms

    if (this.appliedSite) {
      this.updateCharts();
      this.chartsIntervalId = setInterval(() => {
        this.updateCharts();
      }, 300000); // 5 min
    } else {
      this.isLoadingCharts = false;
    }
  }

  updateLiveCounterChartData() {
    this.liveViewCounterChartData.forEach(site => {
      const liveData = this.liveViewData.find(item => item.id === site.id);
      if (!site.chartData) {
        site.chartData = [];
      }

      const count = (site.chartData.at(-1)?.time !== null
        ? ((site.chartData.at(-1).down !== liveData.down
          ? (liveData.down - site.chartData.at(-1).down)
          : 0))
        : liveData.count)
      const newPoint = {
        down: liveData.down,
        time: formatDateToHHmm(new Date(), true),
        count: count
      };

      site.chartData.push(newPoint);
    });
  }

  updateView() {
    if (this.viewSubscription) this.viewSubscription.unsubscribe();

    const viewParams = new HttpParams()
      .set('organisation', this.appliedOrganisation.id)
      .set('site', (this.appliedSite ? this.appliedSite.id : 'ALL'));

    this.viewSubscription = this.commonServices.getLiveView(viewParams).subscribe({
      next: (data: LiveViewDTO[]) => {
        this.liveViewData = data;
        this.updateLiveCounterChartData();
        this.isLoading = false;
      },
      error: () => {
        this.toastr.error(this.translateService.instant("COMMON.GET_DATA_ERROR"), "", toastrConfig);
        this.isLoading = false;
      }
    })
  }

  updateCharts() {
    if (this.chartsSubscription) this.chartsSubscription.unsubscribe();

    const yesterdayDate = new Date();
    yesterdayDate.setDate(yesterdayDate.getDate() - 1);
    const yesterday = yesterdayDate.toISOString();
    const now = new Date().toISOString();

    const statParams = new HttpParams()
      .set('organisation', this.appliedOrganisation.id)
      .set('site', this.appliedSite.id)
      .set('periodStart', yesterday)
      .set('periodEnd', now)
      .set('interval', '5MIN')
      .set('pageSize', 500);

    this.chartsSubscription = this.statisticsService.getStatistics(statParams).subscribe({
      next: (data: StatisticsDTO) => {
        this.liveViewChartsData = data;
        this.isLoadingCharts = false;
      },
      error: () => {
        this.toastr.error(this.translateService.instant("COMMON.GET_DATA_ERROR"), "", toastrConfig);
        this.isLoadingCharts = false;
      }
    })
  }
}
